import type { ComponentType } from "react";
import { Navigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { resolveDashboardVariant, type DashboardVariant } from "@/lib/dashboardVariant";
import Dashboard from "@/pages/Dashboard";
import SuperAdminDashboard from "@/pages/admin/SuperAdminDashboard";
import TesterDashboard from "@/pages/dashboards/TesterDashboard";
import MangelbearbeiterDashboard from "@/pages/dashboards/MangelbearbeiterDashboard";
import { CheckMatyLoader } from "@/components/ui/CheckMatyLoader";
import { MfaChallenge } from "@/components/auth/MfaChallenge";
import { MfaEnrollmentScreen } from "@/components/auth/MfaEnrollmentScreen";
import { RegistrationPendingScreen } from "@/components/auth/RegistrationPendingScreen";

// TES-1093: '/'-Sicht je Rollen-Variante (Owner-Matrix 2026-06-11).
// 'qualitaetsmanager' = Bereichs-/Teamsicht (volles Dashboard); 'generic' =
// Fallback fuer Read-only-Rollen wie customer_approver.
const VARIANT_VIEWS: Record<
  Exclude<DashboardVariant, "admin-redirect" | "qm-admin-redirect">,
  ComponentType
> = {
  "super-admin": SuperAdminDashboard,
  tester: TesterDashboard,
  mangelbearbeiter: MangelbearbeiterDashboard,
  qualitaetsmanager: Dashboard,
  generic: Dashboard,
};

/** Auth-Gates (MFA-Challenge/-Enrollment, Registration-Approval) — null wenn frei. */
function renderAuthGate(auth: ReturnType<typeof useAuth>) {
  if (auth.mfaRequired) return <MfaChallenge onVerified={auth.completeMfa} />;
  if (auth.mfaEnrollmentRequired) return <MfaEnrollmentScreen onComplete={auth.completeMfa} />;
  if (auth.registrationPending) return <RegistrationPendingScreen />;
  return null;
}

export default function HomeRoute() {
  const auth = useAuth();
  const { enabled: landingEnabled, isLoading: flagLoading } = useFeatureFlag(
    "landing-page",
    FEATURE_FLAGS["landing-page"] ?? false,
  );

  if (auth.loading || flagLoading) {
    return <CheckMatyLoader fullScreen />;
  }

  // Not logged in: redirect to landing or auth
  if (!auth.session) {
    return <Navigate to={landingEnabled ? "/landing" : "/auth"} replace />;
  }

  // MFA-/Registration-Gates: block dashboard access until verified/approved
  const gateScreen = renderAuthGate(auth);
  if (gateScreen) {
    return gateScreen;
  }

  // TES-1093: Rollen-Weiche — super_admin-Verhalten aus TES-1092 bleibt
  // unverändert (global → administrative Übersicht, Guest-Mode → Benutzer-
  // verwaltung); admin hat KEIN operatives Dashboard (Owner-Entscheid).
  // TES-2101: qm_admin ist ebenfalls nicht operativ → Landing ist die
  // „braucht Aktion"-Arbeitsliste (/tasks), kein Dashboard.
  const variant = resolveDashboardVariant({
    isSuperAdmin: auth.isSuperAdmin,
    isAdmin: auth.isAdmin,
    isQmAdmin: auth.isQmAdmin,
    isQualitaetsmanager: auth.isQualitaetsmanager,
    isTester: auth.isTester,
    isMangelbearbeiter: auth.isMangelbearbeiter,
    hasActiveTenant: !!auth.profile?.active_tenant_id,
  });

  if (variant === "admin-redirect") {
    return <Navigate to="/admin/users" replace />;
  }

  if (variant === "qm-admin-redirect") {
    return <Navigate to="/tasks" replace />;
  }

  const VariantView = VARIANT_VIEWS[variant];
  return <VariantView />;
}
